import './ProjectItemViewComponent.scss';
import React, { useEffect, useState } from 'react';
import { DataRequestProject } from '../../../../models/DataRequestHub/DataRequestProject';
import { QuestionnaireModuleHost } from '../../../DataRequest/DataRequestDashboardPage/QuestionnaireModuleHost/QuestionnaireModuleHost';
import { DataForm } from '../../../../models/DataRequestHub/DataForm';
import { useStateSelector } from '../../../../store/selectors';
import { useCurrentMarketplaceParty } from '../../../../custom-hooks/useCurrentMarketplaceParty';
import { TimelineParty } from '../../../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import {
    PiqPopupTab,
    PiqPopupTabs,
} from '../../../../slices/marketplace/timeline/models/enums/piq-popup-tab.enum';
import DataTableStagesHost from '../../../DataRequest/DataTableStagesHost/DataTableStagesHost';
import ButtonLoader from '../../../../components/Layout/Buttons/ButtonLoader';
import createNotification from '../../../../utils/createNotification';
import { canMarkDataRequestItemsAsSeen } from '../../../../utils/canMarkDataRequestItemsAsSeen';
import moment from 'moment';

interface ProjectItemViewComponentProps {
    project: DataRequestProject;
    isReadonlyMode: boolean;
    questionnaireId: number;
    questionnaireSnapshotId?: number;
    engagementId?: number;
}

const ProjectItemViewComponent = (props: ProjectItemViewComponentProps) => {
    const axios = useStateSelector((s) => s.core.axios);

    const questionsSeenStatus = useStateSelector(
        (state) => state.dataRequestsNotifications.questionsSeenStatus
    );
    const party = useCurrentMarketplaceParty(props.engagementId);

    const [form, setForm] = useState<DataForm>();
    const [activeTab, setActiveTab] = useState<PiqPopupTab>(
        PiqPopupTab.Questionnaires
    );

    const [isLoading, setIsLoading] = useState(false);

    const tabComponents = [
        {
            id: PiqPopupTab.Questionnaires,
            content: () => (
                <>
                    <div className="questionnaire-header">
                        <div className="right-header">
                            <ButtonLoader
                                type="submit"
                                className={'btn btn-primary'}
                                disabled={isLoading}
                                isLoading={isLoading}
                                loaderButtonText={''}
                                buttonText={'Export to Excel'}
                                onClick={() => {
                                    getQuestionnaireSnapshotFile(form.id);
                                }}
                            />
                        </div>
                    </div>
                    <QuestionnaireModuleHost
                        key={'questionnaire-view-form'}
                        isAdminView={false}
                        projectId={props.project.id}
                        questionnaireForm={form}
                        updateProjectsData={() => {}}
                        projectVariables={props.project.variables}
                        markQuestionsAsSeen={markQuestionsAsSeen}
                        questionsSeenStatus={questionsSeenStatus}
                        project={props.project}
                        onlyShowQuestionColumns={true}
                        isReadonlyMode={props.isReadonlyMode}
                        isBuiltIn={false}
                    />
                </>
            ),
        },
        {
            id: PiqPopupTab.DataTables,
            content: () => (
                <DataTableStagesHost
                    projects={[props.project]}
                    preselectedTableId={0}
                    preselectedTableViewId={0}
                    isAdminView={false}
                    canUseDataTools={false}
                    updateProjectsData={() => {}}
                    markCellsAsSeen={markCellsAsSeen}
                    areTableNamesClickable={false}
                    areActionButtonsHidden={true}
                    hideSnapshots
                    isReadonlyMode={props.isReadonlyMode}
                    engagementId={props.engagementId}
                />
            ),
        },
    ];

    const renderTabContent = () => {
        const activeTabData = tabComponents.find((tab) => tab.id === activeTab);
        return activeTabData ? activeTabData.content() : <></>;
    };

    useEffect(() => {
        setForm({
            ...props.project.dataForms.find(
                (d) =>
                    d.id ===
                    (props.questionnaireSnapshotId ?? props.questionnaireId)
            ),
        });
    }, []);

    const getQuestionnaireSnapshotFile = (questionnaireFormId: number) => {
        setIsLoading(true);
        const targetProject = props.project;

        const requestParamsObj = {
            params: { snapshotFormId: questionnaireFormId },
            responseType: 'blob' as const,
        };
        axios
            .get(
                `api/dataRequestProjects/${targetProject.id}/dataForms/${
                    props.questionnaireSnapshotId ?? props.questionnaireId
                }/dataFormSnapshots/file`,
                requestParamsObj
            )
            .then((response) => {
                if (response.status !== 200) {
                    createNotification(
                        'An error occured while downloading file',
                        'error'
                    );
                } else {
                    const url = window.URL.createObjectURL(
                        new Blob([response.data])
                    );

                    const snapshoDateStringFormat = moment(new Date()).format(
                        'MM-DD-YYYY'
                    );
                    const targetForm = targetProject.dataForms.find(
                        (x) => x.id === questionnaireFormId
                    );
                    const fileName = `${targetProject.practiceName} - ${targetForm.customName} ${snapshoDateStringFormat}.xlsx`;
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', fileName);
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                }
            })
            .finally(() => setIsLoading(false));
    };

    const markQuestionsAsSeen = (
        formId: number,
        questionsIds: number[],
        subQuesitonsIds: number[]
    ) => {
        if (!canMarkDataRequestItemsAsSeen(party)) return;

        const putObject = {
            questionsIds: questionsIds,
            subQuesitonsIds: subQuesitonsIds,
        };

        const targetForm = props.project.dataForms.find((f) => f.id === formId);

        if (
            (!props.project.isSeenByUser || targetForm.isNew) &&
            party === TimelineParty.Seller
        ) {
            axios.put(
                `/api/DataRequestProjects/${props.project.id}/dataForm/${formId}/seenFlag`
            );
        } else {
            axios.put(
                `/api/DataRequestProject/${props.project.id}/dataForm/${formId}/questions/seenFlag`,
                putObject
            );
        }
    };

    const markCellsAsSeen = (
        projectId: number,
        tableId: number,
        viewId: number
    ) => {
        if (!canMarkDataRequestItemsAsSeen(party)) return;

        if (tableId && viewId) {
            axios.put(
                `/api/dataRequestProjects/${projectId}/dataTables/${tableId}/DataTableViews/${viewId}/seenFlag`
            );
        }
    };

    return !form ? (
        <></>
    ) : (
        <div className="body_inner_content piq-popup-container">
            <div className="RRT__container popup-tabs-container">
                <div className="RRT__tabs body-tabs">
                    {PiqPopupTabs?.map((item) => {
                        return (
                            <div
                                key={item.id}
                                className={
                                    activeTab === item.id
                                        ? 'RRT__tab RRT__tab--first RRT__tab--selected'
                                        : 'RRT__tab'
                                }
                                onClick={() => {
                                    setActiveTab(item.id);
                                }}>
                                {item.content}
                            </div>
                        );
                    })}
                </div>
            </div>
            {renderTabContent()}
        </div>
    );
};

export default ProjectItemViewComponent;
